import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2, Lock, Plus, X, Compass } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { STATE_ABBR } from "@/lib/stateMeta";

const STATES = Object.keys(STATE_ABBR).sort();

const EXPERIENCE = ["Brand new", "Beginner", "Intermediate", "Experienced", "Lifelong hunter"];
const SPECIES = ["Elk", "Mule deer", "Whitetail", "Blacktail", "Moose", "Pronghorn", "Turkey", "Bear", "Sheep", "Mountain goat", "Waterfowl", "Upland birds", "Small game", "Predator", "Other"];
const STYLES = ["DIY public land", "DIY private land", "Guided", "Outfitted", "Backcountry", "Base camp", "Day hunting", "Spot and stalk", "Tree stand", "Ground blind", "Waterfowl", "Other"];
const WEAPONS = ["Rifle", "Bow", "Crossbow", "Muzzleloader", "Shotgun", "Handgun", "Traditional archery", "Other"];
const GOALS = ["First elk", "First western hunt", "Trophy mule deer", "Hunt every western state", "Complete a turkey slam", "Take my child hunting", "Hunt with my dad", "DIY elk hunt", "Guided Alaska hunt", "Build preference points"];
const BUDGETS = ["Under $1,000", "$1,000–$2,500", "$2,500–$5,000", "$5,000–$10,000", "$10,000+", "Prefer not to say"];
const HUNT_LENGTHS = ["Day trips", "2–3 days", "4–7 days", "8–14 days", "2+ weeks"];
const TRAVEL = ["Home state only", "Neighboring states", "Anywhere in the West", "Anywhere in the country", "Anywhere in the world"];
const DAYS = ["1–5 days", "6–15 days", "16–30 days", "30+ days"];
const TERRAIN = ["Prairie / plains", "Foothills", "Timber", "High country / alpine", "Marsh / wetland", "Desert", "Mixed"];
const DIFFICULTY = ["Easy / accessible", "Moderate", "Strenuous", "Extreme backcountry"];

const TOTAL = 8;
const EMPTY = {
  first_name: "", last_name: "", home_state: "", region: "", zip_private: "",
  experience_level: "", species: [], hunting_styles: [], weapons: [],
  states_hunt_now: [], states_want: [], goals: [],
  annual_budget_private: "", preferred_hunt_length: "", travel_preference: "",
  days_per_year: "", preferred_terrain: "", physical_difficulty: "",
};

function Chip({ label, active, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`text-sm font-semibold px-4 py-2.5 rounded-full border transition-all duration-150 ${active ? "bg-blaze text-white border-blaze shadow-md scale-[1.02]" : "bg-white/5 text-bone/75 border-white/15 hover:border-blaze/60 hover:text-bone"}`}>
      {active && <Check size={13} className="inline mr-1.5 -mt-0.5" />}{label}
    </button>
  );
}

function ChipGroup({ options, values, onToggle, prefix }) {
  return (
    <div className="flex flex-wrap gap-2.5">
      {options.map((o) => (
        <Chip key={o} label={o} active={values.includes(o)} onClick={() => onToggle(o)}
          testid={`onb-${prefix}-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
      ))}
    </div>
  );
}

function StateMultiSelect({ selected, onToggle, testid }) {
  return (
    <div className="max-h-52 overflow-auto hr-scroll grid grid-cols-2 sm:grid-cols-3 gap-1.5 pr-1" data-testid={testid}>
      {STATES.map((s) => (
        <button key={s} type="button" onClick={() => onToggle(s)}
          className={`text-left text-sm px-3 py-2 rounded border transition-colors ${selected.includes(s) ? "bg-forest text-bone border-forest" : "bg-white/5 text-bone/70 border-white/10 hover:border-forest/60"}`}
          data-testid={`${testid}-${s.toLowerCase().replace(/\s+/g, "-")}`}>
          {selected.includes(s) && <Check size={12} className="inline mr-1 -mt-0.5" />}{s}
        </button>
      ))}
    </div>
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { isAuthed, ready, user } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customGoal, setCustomGoal] = useState("");

  useEffect(() => {
    if (ready && !isAuthed) navigate("/", { replace: true });
  }, [ready, isAuthed, navigate]);

  useEffect(() => {
    if (!isAuthed) return;
    api.homeRangeProfile().then(({ profile }) => {
      const p = profile || {};
      const goals = (p.goals || []).map((g) => g.custom_goal || g.goal_type).filter(Boolean);
      setForm({
        ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, p[k] ?? EMPTY[k]])),
        goals,
        first_name: p.first_name || (user?.display_name || "").split(" ")[0] || "",
      });
      if (p.onboarding_step > 0 && !p.onboarding_complete) setStep(Math.min(TOTAL, p.onboarding_step));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [isAuthed, user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, val) => setForm((f) => ({ ...f, [k]: f[k].includes(val) ? f[k].filter((x) => x !== val) : [...f[k], val] }));

  const payload = (extra = {}) => ({
    ...form,
    goals: form.goals.map((g) => (GOALS.includes(g) ? { goal_type: g } : { goal_type: "custom", custom_goal: g })),
    ...extra,
  });

  const persist = (extra) => api.saveHomeRangeProfile(payload(extra)).catch(() => {});

  const next = async () => {
    if (step < TOTAL) {
      const n = step + 1;
      setStep(n);
      persist({ onboarding_step: n });
    } else {
      setSaving(true);
      try {
        await api.saveHomeRangeProfile(payload({ onboarding_complete: true, onboarding_step: TOTAL }));
        toast.success("Your HomeRange is set up.");
        navigate("/account");
      } catch {
        toast.error("Couldn't save. Please try again.");
        setSaving(false);
      }
    }
  };

  const back = () => step > 1 && setStep(step - 1);

  const skip = async () => {
    await api.saveHomeRangeProfile(payload({ onboarding_complete: true, onboarding_step: step })).catch(() => {});
    toast("You can finish setting up your HomeRange anytime in Settings.");
    navigate("/account");
  };

  const addCustomGoal = () => {
    const g = customGoal.trim();
    if (g && !form.goals.includes(g)) set("goals", [...form.goals, g]);
    setCustomGoal("");
  };

  if (loading || !ready) {
    return <div className="min-h-screen bg-charcoal grid place-items-center"><Loader2 className="animate-spin text-blaze" /></div>;
  }

  const pct = Math.round((step / TOTAL) * 100);

  return (
    <div className="min-h-screen bg-charcoal text-bone grain flex flex-col" data-testid="onboarding-page">
      <header className="max-w-3xl w-full mx-auto px-5 sm:px-8 pt-6 flex items-center justify-between">
        <a href="/"><Logo dark /></a>
        <button onClick={skip} className="text-sm text-bone/50 hover:text-bone transition-colors" data-testid="onboarding-skip">Skip for now</button>
      </header>

      <div className="max-w-3xl w-full mx-auto px-5 sm:px-8 mt-6">
        <div className="flex items-center justify-between text-xs mb-2">
          <span className="hr-overline text-blaze">Build Your HomeRange</span>
          <span className="text-bone/50" data-testid="onboarding-progress-label">Step {step} of {TOTAL}</span>
        </div>
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
          <motion.div className="h-full bg-blaze rounded-full" animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} data-testid="onboarding-progress-bar" />
        </div>
      </div>

      <main className="flex-1 max-w-3xl w-full mx-auto px-5 sm:px-8 py-8">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
            {step === 1 && (
              <div data-testid="onboarding-step-1">
                <h1 className="font-serif text-4xl sm:text-5xl leading-tight">Where is your home range?</h1>
                <p className="text-bone/60 mt-3">This helps HomeRange tailor seasons, regulations and opportunities to you.</p>
                <div className="mt-8 space-y-4 max-w-md">
                  <label className="block">
                    <span className="hr-overline text-bone/50">Home state</span>
                    <select value={form.home_state} onChange={(e) => set("home_state", e.target.value)} className="mt-2 w-full bg-white/5 border border-white/15 rounded px-4 py-3 outline-none focus:border-blaze text-bone" data-testid="onb-home-state">
                      <option value="" className="bg-charcoal">Select your state</option>
                      {STATES.map((s) => <option key={s} value={s} className="bg-charcoal">{s}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="hr-overline text-bone/50">Region <span className="text-bone/30">(optional)</span></span>
                    <input value={form.region} onChange={(e) => set("region", e.target.value)} placeholder="e.g. Western Oregon" className="mt-2 w-full bg-white/5 border border-white/15 rounded px-4 py-3 outline-none focus:border-blaze text-bone placeholder:text-bone/30" data-testid="onb-region" />
                  </label>
                  <label className="block">
                    <span className="hr-overline text-bone/50 flex items-center gap-1.5">ZIP code <Lock size={11} /> <span className="text-bone/30 normal-case tracking-normal font-normal">private — never shown publicly</span></span>
                    <input value={form.zip_private} onChange={(e) => set("zip_private", e.target.value)} placeholder="Optional" className="mt-2 w-full bg-white/5 border border-white/15 rounded px-4 py-3 outline-none focus:border-blaze text-bone placeholder:text-bone/30" data-testid="onb-zip" />
                  </label>
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="onboarding-step-2">
                <h1 className="font-serif text-4xl sm:text-5xl leading-tight">How long have you been hunting?</h1>
                <p className="text-bone/60 mt-3">We&apos;ll match guidance and recommendations to your experience.</p>
                <div className="mt-8 flex flex-col gap-3 max-w-md">
                  {EXPERIENCE.map((e) => (
                    <button key={e} type="button" onClick={() => set("experience_level", e)}
                      className={`text-left px-5 py-4 rounded-lg border transition-all ${form.experience_level === e ? "bg-blaze text-white border-blaze" : "bg-white/5 border-white/15 hover:border-blaze/60"}`}
                      data-testid={`onb-exp-${e.toLowerCase().replace(/\s+/g, "-")}`}>
                      <span className="font-semibold">{e}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="onboarding-step-3">
                <h1 className="font-serif text-4xl sm:text-5xl leading-tight">What do you hunt?</h1>
                <p className="text-bone/60 mt-3">Select all that apply.</p>
                <div className="mt-8"><ChipGroup options={SPECIES} values={form.species} onToggle={(v) => toggle("species", v)} prefix="species" /></div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="onboarding-step-4">
                <h1 className="font-serif text-4xl sm:text-5xl leading-tight">How do you prefer to hunt?</h1>
                <p className="text-bone/60 mt-3">Select all that apply.</p>
                <div className="mt-8"><ChipGroup options={STYLES} values={form.hunting_styles} onToggle={(v) => toggle("hunting_styles", v)} prefix="style" /></div>
              </div>
            )}

            {step === 5 && (
              <div data-testid="onboarding-step-5">
                <h1 className="font-serif text-4xl sm:text-5xl leading-tight">What do you hunt with?</h1>
                <p className="text-bone/60 mt-3">Select all that apply.</p>
                <div className="mt-8"><ChipGroup options={WEAPONS} values={form.weapons} onToggle={(v) => toggle("weapons", v)} prefix="weapon" /></div>
              </div>
            )}

            {step === 6 && (
              <div data-testid="onboarding-step-6">
                <h1 className="font-serif text-4xl sm:text-5xl leading-tight">Where do you hunt?</h1>
                <p className="text-bone/60 mt-3">Split the states you hunt now from the ones on your list.</p>
                <div className="mt-8 grid sm:grid-cols-2 gap-6">
                  <div>
                    <span className="hr-overline text-blaze">Hunt now</span>
                    <p className="text-bone/50 text-xs mb-3 mt-1">States you currently hunt</p>
                    <StateMultiSelect selected={form.states_hunt_now} onToggle={(v) => toggle("states_hunt_now", v)} testid="onb-hunt-now" />
                  </div>
                  <div>
                    <span className="hr-overline text-blaze">Want to hunt</span>
                    <p className="text-bone/50 text-xs mb-3 mt-1">States on your future list</p>
                    <StateMultiSelect selected={form.states_want} onToggle={(v) => toggle("states_want", v)} testid="onb-want" />
                  </div>
                </div>
              </div>
            )}

            {step === 7 && (
              <div data-testid="onboarding-step-7">
                <h1 className="font-serif text-4xl sm:text-5xl leading-tight">What&apos;s on your hunting list?</h1>
                <p className="text-bone/60 mt-3">Pick your goals — or add your own.</p>
                <div className="mt-8"><ChipGroup options={GOALS} values={form.goals} onToggle={(v) => toggle("goals", v)} prefix="goal" /></div>
                {form.goals.filter((g) => !GOALS.includes(g)).length > 0 && (
                  <div className="flex flex-wrap gap-2.5 mt-3">
                    {form.goals.filter((g) => !GOALS.includes(g)).map((g) => (
                      <span key={g} className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-full bg-forest text-bone border border-forest" data-testid={`onb-custom-goal-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        {g}<button type="button" onClick={() => toggle("goals", g)} className="hover:text-blaze"><X size={13} /></button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 mt-5 max-w-md">
                  <input value={customGoal} onChange={(e) => setCustomGoal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomGoal())} placeholder="Add a custom goal" className="flex-1 bg-white/5 border border-white/15 rounded px-4 py-2.5 outline-none focus:border-blaze text-bone placeholder:text-bone/30" data-testid="onb-custom-goal-input" />
                  <button type="button" onClick={addCustomGoal} className="bg-white/10 hover:bg-blaze hover:text-white rounded px-4 transition-colors" data-testid="onb-custom-goal-add"><Plus size={18} /></button>
                </div>
              </div>
            )}

            {step === 8 && (
              <div data-testid="onboarding-step-8">
                <h1 className="font-serif text-4xl sm:text-5xl leading-tight">A little personalization.</h1>
                <p className="text-bone/60 mt-3 flex items-center gap-1.5"><Lock size={13} /> Financial details are always private.</p>
                <div className="mt-8 grid sm:grid-cols-2 gap-5">
                  {[
                    ["annual_budget_private", "Typical annual hunting budget", BUDGETS, "budget"],
                    ["preferred_hunt_length", "Preferred hunt length", HUNT_LENGTHS, "length"],
                    ["travel_preference", "How far will you travel?", TRAVEL, "travel"],
                    ["days_per_year", "Days afield per year", DAYS, "days"],
                    ["preferred_terrain", "Preferred terrain", TERRAIN, "terrain"],
                    ["physical_difficulty", "Physical difficulty", DIFFICULTY, "difficulty"],
                  ].map(([key, label, opts, tid]) => (
                    <label key={key} className="block">
                      <span className="hr-overline text-bone/50">{label}</span>
                      <select value={form[key]} onChange={(e) => set(key, e.target.value)} className="mt-2 w-full bg-white/5 border border-white/15 rounded px-4 py-3 outline-none focus:border-blaze text-bone" data-testid={`onb-${tid}`}>
                        <option value="" className="bg-charcoal">Select…</option>
                        {opts.map((o) => <option key={o} value={o} className="bg-charcoal">{o}</option>)}
                      </select>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      <footer className="sticky bottom-0 bg-charcoal/95 backdrop-blur border-t border-white/10">
        <div className="max-w-3xl w-full mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <button onClick={back} disabled={step === 1} className="inline-flex items-center gap-2 text-sm font-semibold text-bone/60 hover:text-bone disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-testid="onboarding-back">
            <ArrowLeft size={16} /> Back
          </button>
          <button onClick={next} disabled={saving} className="inline-flex items-center gap-2 bg-blaze text-white px-7 py-3 rounded-full font-semibold hover:bg-blaze-light transition-colors disabled:opacity-50" data-testid="onboarding-next">
            {saving ? <Loader2 size={16} className="animate-spin" /> : step === TOTAL ? <>Enter My HomeRange <Compass size={16} /></> : <>Continue <ArrowRight size={16} /></>}
          </button>
        </div>
      </footer>
    </div>
  );
}
